<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, watch, onUnmounted } from 'vue';
import InfoWindow from '../map/InfoWindow.vue';
import BaseButton from '../base/BaseButton.vue';
import Legend from '../map/Legend.vue';
import BaseSelect from '@/components/base/BaseSelect.vue';
import BottomWindows from '@/components/map/BottomWindows.vue';
import { getLabel, getLocale } from '@/utils/data-display-utils';
import { storeToRefs } from 'pinia';

import mapboxgl from 'mapbox-gl';
import { MapboxSearchBox } from '@mapbox/search-js-web';
import * as turf from '@turf/turf';
import { getTranslation } from '@/utils/data-display-utils';
import { addGeoJsonOverlay, setGeoJsonOverlayVisibility } from '@/utils/map-layers';
import { coverZoom, type BBox } from '@/utils/map-bounds';
import {
  addCategoryPointLayers,
  bottomPointLayerId,
  categoryMarkerImageUrl,
  createPointGeoJson,
  loadCategoryMarkerImages,
  setCategoryPointsVisibility,
  setPointCollectionData,
  unclusteredPointLayerId,
} from '@/utils/map-points';
import useResponsive from '@/utils/responsive-util';
import { useCommunityStore } from '@/store/communityStore';
import { useUserStore } from '@/store/userStore';
import { Config } from '@/config';
import { Locales } from '@/store/language.interface';
import type { LngLatTuple, MapPoint } from '@/components/map/map.types';

// PROPS
const props = defineProps<{
  showInfo?: boolean;
  id: string;
  mapConfig: any;
  points: MapPoint[];
  isFullscreen?: boolean;
  showQuestionResults: boolean;
  allowPointPlacement: boolean;
  /** Greys out the button that adds a point, which `allowPointPlacement` shows. */
  pointPlacementDisabled?: boolean;
  showLayerToggles?: boolean;
  hideAddressSearch?: boolean;
  /** The fullscreen button, drawn inside the map rather than in the toolbar above it. */
  showFullscreenControl?: boolean;
  /** Draws the map 16:9, the shape of a Video module, instead of 400px tall. */
  videoAspectRatio?: boolean;
  languageEnabled?: Locales[];
}>();

// EMITS
const emit = defineEmits<{
  (e: 'showInfo'): void;
  (
    e: 'pointPlaced',
    point: { latitude: number; longitude: number; category: number | null; label: string | null },
  ): void;
  (e: 'pointAction', properties: Record<string, any>): void;
  (e: 'verifyUser'): void;
  (e: 'mapStart'): void;
  (e: 'openFullscreen'): void;
  (e: 'exitFullscreen'): void;
}>();

// CONST
const accessToken = Config.mapbox.accessToken;
const userEditing = ref<boolean>(false);
const addingData = ref<boolean>(false);
const legendVisible = ref<boolean>(false);
const userComment = ref<string>();
const markerCategory = ref(null);
const markerInputCategory = ref(null);
const userMarkerCoordinates = ref<LngLatTuple | null>(null);
// Filled in place on load: the hover listeners below hold this array by reference, so reassigning
// it would leave them watching an empty list.
const layerIds: string[] = [];
const { isMobile } = useResponsive();
const locale = computed(() => getLocale(props.languageEnabled));
const communityStore = useCommunityStore();
const userStore = useUserStore();
const bounds: [LngLatTuple, LngLatTuple] = [
  props.mapConfig.bounds.southWest, // SouthWest coordinate [Longitude, Latitude]
  props.mapConfig.bounds.northEast, // NorthEast coordinate [Longitude, Latitude]
];
const { community } = storeToRefs(communityStore);
// Flatten your bounds into a bbox array [minLng, minLat, maxLng, maxLat]
const bbox: BBox = [
  bounds[0][0], // minLng (SouthWest Longitude)
  bounds[0][1], // minLat (SouthWest Latitude)
  bounds[1][0], // maxLng (NorthEast Longitude)
  bounds[1][1], // maxLat (NorthEast Latitude)
];
const center = turf.getCoord(turf.center(turf.bboxPolygon(bbox)));
const initialZoom = props.mapConfig.zoom;
const categories = computed(() => props.mapConfig?.categories ?? []);
const overlayLayers = ref(
  (props.mapConfig?.layers ?? []).map((layer) => ({ ...layer, visible: layer.defaultVisibility !== false })),
);

let map;
let searchBox;
let markerLocation: LngLatTuple;
let userMarker;
let tempMarker;
let searchBoxObserver;
let containerObserver: ResizeObserver | null = null;
const markerSelected = ref(null);

const legendTitleKey = computed(() => (categories.value.length ? 'map.legend' : 'map.layers.title'));

const isPointPlacementEnabled = computed(() => props.allowPointPlacement && !props.showQuestionResults);

const isPlacingMarker = computed(() => userEditing.value || addingData.value);

function addMarkerToMap(coord: LngLatTuple) {
  userEditing.value = false;
  addingData.value = true;
  const el = document.createElement('div');
  el.className = 'tempMarker';
  el.style.backgroundImage = `url(${categoryMarkerImageUrl('gray')})`;
  el.style.width = '30px';
  el.style.height = '40px';
  el.style.backgroundSize = '100%';

  tempMarker = new mapboxgl.Marker(el).setLngLat(coord).addTo(map);

  markerLocation = coord;
}

const pointCollections = ref(createPointGeoJson(categories.value, props.points));

// LIFECYCLE
onMounted(() => {
  initMap();

  document.addEventListener('keydown', handleOutsideClick);
  document.addEventListener('click', handleOutsideClick);

  map.on('click', (e) => {
    if (legendVisible.value || props.showInfo) {
      resetMapWindows();
    }
  });

  map.on('load', (e) => {
    map.resize();
    setMapLang(locale.value);
    loadCategoryMarkerImages(map, categories.value);

    layerIds.push(
      ...addCategoryPointLayers(map, pointCollections.value, {
        clusterEnabled: props.mapConfig.clusterEnabled === true,
        onPointClick: (features) => {
          resetMapWindows();
          resetMapMarkers();
          markerSelected.value = features;
        },
      }),
    );

    try {
      const beneath = bottomPointLayerId(map, pointCollections.value);
      // A layer is drawn once its file arrives: reapply its visibility then, in case the participant
      // toggled it in the meantime.
      overlayLayers.value.forEach((layer) =>
        addGeoJsonOverlay(map, layer, beneath).then(() => setGeoJsonOverlayVisibility(map, layer.id, layer.visible)),
      );
    } catch (err) {
      console.error('Error adding custom layers', err);
    }
  });

  map.on('mouseenter', layerIds, (e) => {
    map.getCanvas().style.cursor = 'pointer';
  });

  map.on('mouseleave', layerIds, () => {
    map.getCanvas().style.cursor = '';
  });

  userMarker = new mapboxgl.Marker({
    color: '#808080',
    draggable: true,
  });

  userMarker.on('dragend', onDragEnd);

  map.addControl(new mapboxgl.NavigationControl());
  map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-left');

  if (isMobile.value) {
    map.scrollZoom.disable();
  }

  const container = map.getContainer();
  if (typeof ResizeObserver !== 'undefined' && container) {
    containerObserver = new ResizeObserver(() => {
      resizeMap();
    });
    containerObserver.observe(container);
  }
});

onBeforeUnmount(() => {
  if (map) {
    map.remove();
  }
  if (searchBox) {
    searchBox.remove();
  }
  if (searchBoxObserver) {
    searchBoxObserver.disconnect();
    searchBoxObserver = null;
  }
  if (containerObserver) {
    containerObserver.disconnect();
    containerObserver = null;
  }
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleOutsideClick);
  document.removeEventListener('click', handleOutsideClick);
});
// FUNCTIONS
function minimumZoom() {
  const container = map.getContainer();
  const cover = coverZoom(bbox, container.clientWidth, container.clientHeight);
  // `setMinZoom` throws above the map's max zoom, which a very small framed area would ask for.
  return Math.min(Math.max(initialZoom, cover ?? initialZoom), map.getMaxZoom());
}

function applyBoundsConstraint() {
  if (!map) return;
  map.setMinZoom(minimumZoom());
}

function initMap() {
  map = new mapboxgl.Map({
    accessToken: accessToken,
    container: props.id,
    style: props.mapConfig.mapStyle,
    center: center, // can also pull location out of browser
    zoom: initialZoom,
    attributionControl: false,
    bounds: bounds,
    maxBounds: bounds,
    // Mapbox styles default to the globe projection, under which `maxBounds` clamps only the map's
    // center, leaving half a viewport of room past every edge. Mercator clamps the whole view.
    projection: 'mercator',
  });

  applyBoundsConstraint();

  initSearch();
}

function initSearch(lang = '') {
  // The toolbar renders no container for it, and the language watcher calls this too.
  if (props.hideAddressSearch) return;
  if (lang === '') {
    lang = locale.value;
  }
  if (searchBox) {
    searchBox.remove();
    searchBox = null;
  }
  if (searchBoxObserver) {
    searchBoxObserver.disconnect();
    searchBoxObserver = null;
  }
  searchBox = new MapboxSearchBox();
  searchBox.accessToken = accessToken;
  searchBox.options = {
    language: lang,
    bbox: bounds,
  };
  searchBox.mapboxgl = mapboxgl;
  searchBox.marker = true;
  searchBox.placeholder = getLabel('map.search.placeholder', props.languageEnabled);
  searchBox.bindMap(map.container);

  searchBox.addEventListener('retrieve', (event) => {
    userMarker.remove();
    const addressPoint = turf.getCoord(turf.center(event.detail)) as LngLatTuple;
    if (userEditing.value) {
      addMarkerToMap(addressPoint);
      map.flyTo({ center: addressPoint, zoom: 14 });
    } else {
      map.flyTo({ center: addressPoint, zoom: 14 });
    }
  });
  const searchBoxContainer = document.getElementById(`search-box-container ${props.id}`);
  searchBoxContainer.appendChild(searchBox);
  patchSearchBoxAccessibility(searchBoxContainer);
}

function patchSearchBoxAccessibility(searchBoxContainer: HTMLElement) {
  const syncSearchA11y = () => {
    const combobox = searchBoxContainer.querySelector<HTMLInputElement>('input[role="combobox"]');
    if (!combobox) return;

    const resultsList = searchBoxContainer.querySelector<HTMLElement>('[id$="ResultsList"]');
    const isExpanded = Boolean(
      resultsList && !resultsList.hasAttribute('hidden') && resultsList.getAttribute('aria-hidden') !== 'true',
    );

    combobox.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    combobox.setAttribute('aria-label', getLabel('map.search.label', props.languageEnabled));
  };

  syncSearchA11y();
  searchBoxObserver = new MutationObserver(() => {
    syncSearchA11y();
  });
  searchBoxObserver.observe(searchBoxContainer, {
    attributes: true,
    childList: true,
    subtree: true,
    attributeFilter: ['hidden', 'aria-hidden'],
  });
}

const userHasDemographicsData = computed(() => {
  return userStore.validateDemographicData;
});

const isUserAllowedToAddMarker = computed(
  () => userStore.isUserLogged && (!community.value.options.use_demographics || userHasDemographicsData.value),
);

function addMarkerBtnClick() {
  emit('mapStart');
  if (isUserAllowedToAddMarker.value) {
    userEditing.value = !userEditing.value;
    if (!userEditing.value) {
      userMarker.remove();
    } else {
      userMarkerCoordinates.value = [map.getCenter().lng, map.getCenter().lat];
      userMarker.setLngLat(map.getCenter()).addTo(map);
      resetMapWindows();
      userEditing.value = true;
      if (props.showInfo) {
        emit('showInfo');
      }
    }

    if (userMarkerCoordinates.value && addingData.value) {
      resetMapMarkers();
      addingData.value = !addingData.value;
      userEditing.value = false;
    }
  } else {
    emit('verifyUser');
  }
}

function onDragEnd() {
  const coords = userMarker.getLngLat();
  userMarkerCoordinates.value = [coords.lng, coords.lat];
}

function toggleLegend() {
  legendVisible.value = !legendVisible.value;
}

function panHome() {
  map.flyTo({ center: center, zoom: minimumZoom() });
}

function toggleFullscreen() {
  if (props.isFullscreen) {
    emit('exitFullscreen');
  } else {
    emit('openFullscreen');
  }
}

function handleOutsideClick(event) {
  const mapDiv = document.getElementById(props.id);

  if (event.key === 'Escape') {
    resetMapMarkers();
    resetMapWindows();
  }

  if (event.type === 'click' && !mapDiv.contains(event.target)) {
    if (legendVisible.value) {
      legendVisible.value = false;
    }
  }
}

function resetMapWindows() {
  legendVisible.value = false;
  addingData.value = false;
  userEditing.value = false;
  markerSelected.value = false;
  emit('showInfo');
}

function resetMapMarkers() {
  if (userMarker) {
    userMarker.remove();
  }
  if (tempMarker) {
    tempMarker.remove();
  }
}

function resetMarkerData() {
  userComment.value = '';
  markerCategory.value = null;
}

function changeMarkerCategory(e) {
  const category = categories.value.find((c) => c.id === e.value);
  markerCategory.value = category.id;
}

function setUserComment(comment) {
  userComment.value = comment;
  submitData();
}

function submitData() {
  if (categories.value.length === 1) {
    markerCategory.value = categories.value[0].id;
  }

  emit('pointPlaced', {
    longitude: markerLocation[0],
    latitude: markerLocation[1],
    category: markerCategory.value,
    label: userComment.value ?? null,
  });

  const tempMarkers = document.getElementsByClassName('tempMarker');
  Array.from(tempMarkers).forEach((element) => {
    element.remove();
  });

  addingData.value = false;
  resetMarkerData();
}

function setUserMarker() {
  if (userMarkerCoordinates.value === null) {
    userMarkerCoordinates.value = [map.getCenter().lng, map.getCenter().lat];
  }
  addingData.value = true;
  userEditing.value = false;
  userMarker.remove();
  addMarkerToMap(userMarkerCoordinates.value);
}

const toggleLayerVisibility = (legendLayer) => {
  if (!map) return;
  const visibility = map.getLayoutProperty(unclusteredPointLayerId(legendLayer.id), 'visibility');
  setCategoryPointsVisibility(map, legendLayer.id, visibility !== 'visible');
};

const toggleOverlayLayerVisibility = (layer) => {
  layer.visible = !layer.visible;
  setGeoJsonOverlayVisibility(map, layer.id, layer.visible);
};

function resizeMap() {
  map.resize();
  applyBoundsConstraint();
}

function setMapLang(lang) {
  map.getStyle().layers.forEach((layer) => {
    if (layer.id.endsWith('-label')) {
      map.setLayoutProperty(layer.id, 'text-field', ['coalesce', ['get', `name_${lang}`], ['get', 'name']]);
    }
  });
}

function closeMarkerPopup() {
  markerSelected.value = null;
}

function closeInfoPopup() {
  emit('showInfo');
}

function closeAddingPopup() {
  addingData.value = false;
  resetMapMarkers();
}

function closeEditingPopup() {
  userEditing.value = false;
  resetMapMarkers();
}

// WATCH
watch(locale, (newValue) => {
  setMapLang(newValue);
  initSearch(newValue);
});

watch(
  () => props.showInfo,
  () => {
    if (props.showInfo) {
      markerSelected.value = null;
      addingData.value = false;
      legendVisible.value = false;
      userEditing.value = false;
      resetMapMarkers();
    }
  },
);

watch(
  () => props.points,
  (points) => {
    pointCollections.value = createPointGeoJson(categories.value, points);
    if (!map) return;
    pointCollections.value.forEach((collection) => setPointCollectionData(map, collection));
    refreshSelectedMarker();
  },
  { deep: true },
);

function refreshSelectedMarker() {
  const selected = markerSelected.value;
  if (!Array.isArray(selected) || !selected.length) return;
  const selectedId = selected[0]?.properties?.pointId;
  if (selectedId == null) return;
  for (const collection of pointCollections.value) {
    const match = collection.geoJson.features.find((f) => f.properties?.pointId === selectedId);
    if (match) {
      markerSelected.value = [match];
      return;
    }
  }
  markerSelected.value = null;
}

const pointAction = () => {
  if (props.showQuestionResults) return;
  if (!userStore.isUserLogged) {
    emit('verifyUser');
    return;
  }
  const properties = markerSelected.value?.[0]?.properties;
  if (!properties) return;
  emit('pointAction', properties);
};
</script>
<template>
  <div
    :id="props.id"
    class="map base-map"
    :class="{
      fullscreenMap: props.isFullscreen,
      'with-fullscreen-control': props.showFullscreenControl,
      'video-aspect-ratio': props.videoAspectRatio,
    }"
  >
    <div class="top-controls">
      <BaseButton
        class="top"
        isIconOnly
        icon="layers"
        :ariaLabel="getLabel('map.toolbar.layers', props.languageEnabled)"
        @click="toggleLegend"
      />
    </div>
    <div class="home-control">
      <BaseButton
        class="top"
        isIconOnly
        icon="o_home"
        :ariaLabel="getLabel('map.toolbar.home', props.languageEnabled)"
        @click="panHome"
      />
    </div>
    <div v-if="props.showFullscreenControl" class="fullscreen-control">
      <BaseButton
        class="top"
        isIconOnly
        :icon="props.isFullscreen ? 'fullscreen_exit' : 'fullscreen'"
        :ariaLabel="
          getLabel(props.isFullscreen ? 'map.toolbar.exit-fullscreen' : 'map.toolbar.fullscreen', props.languageEnabled)
        "
        @click="toggleFullscreen"
      />
    </div>
    <InfoWindow v-show="legendVisible" anchor-legend>
      <template v-slot:title>{{ getLabel(legendTitleKey, props.languageEnabled) }}</template>
      <template v-slot:content>
        <div id="menu">
          <Legend
            v-if="categories.length"
            :layers="categories"
            :language-enabled="props.languageEnabled"
            @update-visibility="toggleLayerVisibility($event)"
          />
          <div class="map-overlay-layers" v-if="props.showLayerToggles">
            <div class="map-overlay-layers__title" v-if="categories.length">
              {{ getLabel('map.layers.title', props.languageEnabled) }}
            </div>
            <div class="map-overlay-layers__empty" v-if="!overlayLayers.length">
              {{ getLabel('map.layers.empty', props.languageEnabled) }}
            </div>
            <div class="map-overlay-layers__item" v-for="layer in overlayLayers" :key="layer.id">
              <q-checkbox
                size="xs"
                :model-value="layer.visible"
                :label="getTranslation(layer, 'name', props.languageEnabled)"
                @update:model-value="toggleOverlayLayerVisibility(layer)"
              />
            </div>
          </div>
        </div>
      </template>
    </InfoWindow>
    <InfoWindow v-if="props.isFullscreen && props.showInfo" anchor-top @close="closeInfoPopup">
      <template v-slot:title>{{ getLabel('map.instructions.title', props.languageEnabled) }}</template>
      <template v-slot:content>
        <div>{{ getLabel('map.instructions.content', props.languageEnabled) }}</div>
      </template>
      <template v-slot:close>
        <q-icon name="close" @click="closeInfoPopup" />
      </template>
    </InfoWindow>
    <InfoWindow v-if="props.isFullscreen && userEditing && !props.showInfo" anchor-bottom @close="closeEditingPopup">
      <template v-slot:title>{{ getLabel('map.add-marker.title', props.languageEnabled) }}</template>
      <template v-slot:content>
        <div>{{ getLabel('map.add-marker.content', props.languageEnabled) }}</div>
        <BaseButton
          id="set-location"
          class="action set-location"
          icon="location_searching"
          :label="getLabel('map.add-marker.button', props.languageEnabled)"
          @click="setUserMarker"
        />
      </template>
      <template v-slot:close>
        <q-icon name="close" @click="closeEditingPopup" />
      </template>
    </InfoWindow>
    <InfoWindow v-if="props.isFullscreen && addingData && !props.showInfo" anchor-bottom @close="closeAddingPopup">
      <template v-slot:title>{{ getLabel('map.selected-point.title', props.languageEnabled) }}</template>
      <template v-slot:content>
        <div>{{ getLabel('map.selected-point.content', props.languageEnabled) }}</div>
        <div v-if="categories.length > 1">
          <div>{{ getLabel('map.selected-point.category', props.languageEnabled) }}</div>
          <base-select
            :ref="markerInputCategory"
            :options="
              categories.map((category) => ({
                label: getTranslation(category, 'name', languageEnabled),
                value: category.id,
              }))
            "
            @update="changeMarkerCategory($event)"
          />
        </div>
        <div>
          <div>{{ getLabel('map.selected-point.comment', props.languageEnabled) }}</div>
          <q-input v-model="userComment" outlined />
        </div>
        <BaseButton class="action" :label="getLabel('buttons.submit', props.languageEnabled)" @click="submitData" />
      </template>
      <template v-slot:close>
        <q-icon name="close" @click="closeAddingPopup" />
      </template>
    </InfoWindow>
    <div class="bottom-controls" v-if="isPointPlacementEnabled && !isPlacingMarker">
      <q-btn
        class="bottom-controls__button"
        no-caps
        flat
        :label="getLabel('map.add-marker.title', props.languageEnabled)"
        icon="o_add_location"
        :disable="props.pointPlacementDisabled"
        @click="addMarkerBtnClick"
      />
    </div>
  </div>
  <BottomWindows
    :is-fullscreen="props.isFullscreen"
    :info-visible="props.showInfo"
    :user-editing="userEditing"
    :adding-data="addingData"
    :marker-selected="markerSelected"
    :map-config="props.mapConfig"
    :language-enabled="props.languageEnabled"
    @set-marker="setUserMarker"
    @set-user-comment="setUserComment($event)"
    @change-category="changeMarkerCategory($event)"
    @close-marker="closeMarkerPopup"
    @close-info="closeInfoPopup"
    @close-adding="closeAddingPopup"
    @close-editing="closeEditingPopup"
    @point-action="pointAction"
  >
    <template #marker-content="slotProps">
      <slot name="marker-content" v-bind="slotProps" />
    </template>
  </BottomWindows>
</template>
<style lang="scss">
.map-overlay-layers {
  display: flex;
  flex-direction: column;
  padding-top: $space-sm;
  gap: $space-xs;

  &__title {
    font-weight: 600;
  }

  &__empty {
    color: $color-neutral-grey-400;
  }

  &__item {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
}
.marker {
  display: block;
  border: none;
  cursor: pointer;
  padding: 0;
}
.map {
  width: 100%;
  position: relative;
  overflow: hidden;
  isolation: isolate;

  &.base-map {
    height: 400px;
  }

  // Matches `VideoPlayer`'s 16 / 9 and the modern theme's `aspect-video`.
  &.base-map.video-aspect-ratio {
    height: auto;
    aspect-ratio: 16 / 9;
  }

  &.fullscreenMap {
    height: 92vh !important;
  }

  .mapboxgl-ctrl-top-right,
  .mapboxgl-ctrl-bottom-left {
    z-index: 1;
  }

  .home-control,
  .fullscreen-control {
    display: flex;
    flex-direction: row;
    position: absolute;
    justify-content: center;
    align-items: center;
    top: 96px;
    right: 10px;
    width: 29px;
    z-index: 1;
    box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;

    .q-btn {
      width: 100%;
      padding: 0;
      background-color: $color-neutral-white;
      border-radius: 5px !important;

      .q-icon {
        font-size: 20px !important;
      }
    }
  }

  /* The fullscreen button heads the right-hand controls, then Mapbox's zoom buttons, then home, each
     10px apart as Mapbox spaces its own controls. */
  &.with-fullscreen-control {
    $gap: 10px;
    $button: 36px; // the fullscreen and home buttons
    $zoom-group: 87px; // Mapbox's zoom in, zoom out and compass, 29px each

    .fullscreen-control {
      top: $gap;
    }

    // Mapbox's corner container: its group keeps its own 10px margin inside it.
    .mapboxgl-ctrl-top-right {
      top: $gap + $button;
    }

    .home-control {
      top: $gap + $button + $gap + $zoom-group + $gap;
    }
  }

  .top-controls {
    display: flex;
    flex-direction: row;
    position: absolute;
    top: 10px;
    left: 10px;
    width: fit-content;
    background-color: $color-neutral-white;
    z-index: 1;
    border-radius: 6px;
    box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;

    button {
      width: 40px;
      color: $color-primary-darken;

      &:hover {
        background-color: transparent;
      }
    }

    .q-separator {
      z-index: 2;
    }
  }

  .bottom-controls {
    display: flex;
    flex-direction: row;
    position: absolute;
    bottom: 10px;
    right: 10px;
    width: fit-content;
    z-index: 1;
    border-radius: 6px;
    box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;

    &__button {
      padding: 0px 8px;
      background-color: $color-neutral-white;
      color: $color-primary-darken;
      border-radius: 8px;

      .q-icon {
        margin-right: 8px;
      }

      // Quasar only fades a disabled button, which would keep it in the primary colour.
      &.disabled {
        color: $color-neutral-grey-400;
      }
    }
  }

  .set-location {
    background-color: $color-primary;
    color: $color-neutral-white;
    border-radius: 8px;
    padding: 0px 8px;

    .q-icon {
      margin: $space-sm 0;
    }
  }

  .mapboxgl-ctrl-group:not(:empty) {
    box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;

    button {
      color: $color-primary-darken;
    }
  }

  .type_select {
    border: 1px solid black;
  }
}
.infosMarker_wrapper {
  display: flex;
  flex-direction: row;
  width: 100%;

  .infosMarker {
    width: 100%;

    &_content {
      display: flex;
      flex-direction: row;
      justify-content: space-between;

      &-actions {
        display: flex;
        flex-direction: column;

        .q-btn {
          padding: 0;
        }

        .q-icon {
          margin: 0 0 0 $space-sm;
        }
      }
    }

    &_infos {
      display: flex;
      flex-direction: row;
      justify-content: space-between;

      p {
        margin: 0;
      }
    }
  }

  .close {
    display: flex;
    justify-content: center;
    width: auto;
    margin: 0 0 0 $space-sm;

    .q-btn {
      height: 30px;
      width: 36px;
      padding: 0;
    }
  }
}
</style>
